import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { signedInWithMagicLink } from "@/lib/authMethod";

export function usePasswordChangeRequired() {
  const { user, session } = useAuth();
  const [mustChangePassword, setMustChangePassword] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  // Track if we've fetched once to avoid loading flash on refetch.
  // When loading=true, ProtectedRoute shows a spinner and UNMOUNTS children,
  // which destroys all React state (wizard step, form data, etc.).
  const hasFetchedOnce = useRef(false);

  // Derived as a boolean rather than depending on the raw token: `amr` survives
  // token refresh, so this stays stable and the callback below is not recreated
  // (and no refetch is triggered) every time Supabase rotates the access token.
  const magicLinkSession = useMemo(
    () => signedInWithMagicLink(session?.access_token),
    [session?.access_token]
  );

  const fetchPasswordChangeStatus = useCallback(async () => {
    if (!user) {
      setMustChangePassword(null);
      setLoading(false);
      return;
    }

    const signupChannel = typeof user.user_metadata?.signup_channel === "string"
      ? user.user_metadata.signup_channel
      : null;

    // A magic-link sign-in goes straight through, whatever the account's origin.
    // This is deliberately NOT persisted to the profile: the account still needs
    // a password set, we're only skipping the prompt for this visit. Signing in
    // with the password later still prompts, which is the behaviour Bridget
    // described (VA-192).
    if (magicLinkSession) {
      setMustChangePassword(false);
      setLoading(false);
      hasFetchedOnce.current = true;
      return;
    }

    if (signupChannel === "public_magic_link" || signupChannel === "shared_brand_guide") {
      const firstName = typeof user.user_metadata?.first_name === "string" ? user.user_metadata.first_name.trim() : "";
      const lastName = typeof user.user_metadata?.last_name === "string" ? user.user_metadata.last_name.trim() : "";
      const displayName = typeof user.user_metadata?.display_name === "string"
        ? user.user_metadata.display_name.trim()
        : [firstName, lastName].filter(Boolean).join(" ").trim();

      setMustChangePassword(false);
      setLoading(false);
      hasFetchedOnce.current = true;

      supabase
        .from("profiles")
        .upsert(
          {
            user_id: user.id,
            display_name: displayName || null,
            first_name: firstName || null,
            last_name: lastName || null,
            must_change_password: false,
          },
          { onConflict: "user_id" }
        )
        .then(({ error }) => {
          if (error) {
            console.warn("[usePasswordChangeRequired] Failed to normalize magic-link signup profile:", error);
          }
        });

      return;
    }

    // Only show loading spinner on initial fetch, not on refetches.
    // This prevents ProtectedRoute from unmounting children (BrandGuide, etc.)
    // when Supabase token refresh causes a user reference change.
    if (!hasFetchedOnce.current) {
      setLoading(true);
    }
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("must_change_password")
        .eq("user_id", user.id)
        .maybeSingle();

       if (error) {
         setMustChangePassword(false);
       } else {
        const dbValue = data?.must_change_password ?? false;
        setMustChangePassword(dbValue);
      }
     } catch (_err) {
       setMustChangePassword(false);
     } finally {
      setLoading(false);
      hasFetchedOnce.current = true;
    }
  }, [user?.id, magicLinkSession]);

  useEffect(() => {
    fetchPasswordChangeStatus();
  }, [fetchPasswordChangeStatus]);

  return { mustChangePassword, loading, refetch: fetchPasswordChangeStatus };
}
